import type { Entries } from './catalog';

/**
 * The words of the cloud packages allowances page, for Beyond operators, in both languages: an allowance is
 * "allowance" («asignación»), storage is said in MiB as its terms are written, and usage is measured, never charged.
 * Kept apart so each language's catalog stays readable; `en` and `es` spread them.
 */
export const operator: { en: Entries; es: Entries } = {
	en: {
		'nav.operator': 'Allowances',
		'page.operator': 'Cloud packages allowances · Beyond Snapshots',
		'loading.operator': 'the allowance',
		'operator.title': 'Cloud packages allowances',
		'operator.lead': 'Turn cloud packages on for an organization, change their terms or turn them off.',
		'operator.denied': 'Only a Beyond operator can open this page',
		'operator.denied.reason': 'Your account is not a Beyond operator at Beyond Accounts.',
		'operator.organization': 'Organization',
		'operator.organization.hint': 'Its identifier at Beyond Accounts, such as org_…',
		'operator.organization.show': 'Show organization',
		'operator.organization.wrong': 'Write an identifier such as org_…, with letters and digits after the underscore.',
		'operator.organization.none': 'Write an organization’s identifier to see its allowance.',
		'operator.state.active': 'Active at Beyond Accounts.',
		'operator.state.closing': 'Closing at Beyond Accounts: no allowance can be granted.',
		'operator.state.closed': 'Closed at Beyond Accounts: no allowance can be granted.',
		'operator.state.unknown': 'Beyond Accounts doesn’t know this organization as one that uses Snapshots, so no allowance can be granted. A member must sign in to Snapshots first.',
		'operator.state.unavailable': 'Beyond Accounts didn’t say where this organization stands, so no allowance can be granted right now.',
		'operator.signed': 'You signed in to Beyond {time}. Changing an allowance needs a sign-in from the last {minutes} minutes.',
		'operator.signed.unknown': 'Beyond Accounts didn’t say when you signed in. Changing an allowance needs a sign-in from the last {minutes} minutes.',
		'operator.section.current': 'Allowance in force',
		'operator.current.none': 'No allowance. Cloud packages are off for this organization; its packages can still be read, downloaded and deleted.',
		'operator.current.past': 'This allowance ended by itself on {date}. Cloud packages are off until another is granted.',
		'operator.fact.plan': 'Plan',
		'operator.fact.bytes': 'Storage',
		'operator.fact.sources': 'Packages',
		'operator.fact.granted': 'Granted',
		'operator.fact.granted.value': '{date} by {person}',
		'operator.fact.ends': 'Ends',
		'operator.fact.ends.none': 'When an operator ends it',
		'operator.fact.reason': 'Reason',
		'operator.limits.note': 'These are the most this organization can keep. The installation allows at most {bytes} and {sources} packages.',
		'operator.section.usage': 'Measured usage',
		'operator.usage.month': 'Month',
		'operator.usage.lead': 'What this organization used in {period}, read {time}. Usage is measured; nothing is charged.',
		'operator.usage.empty': 'Nothing was measured in {period}.',
		'operator.usage.what': 'What',
		'operator.usage.quantity': 'Quantity',
		'operator.usage.internal': '{kind} (the service’s own storage work)',
		'operator.section.history': 'History',
		'operator.history.empty': 'This organization was never given an allowance.',
		'operator.history.granted': '{plan}, granted {date} by {person}',
		'operator.history.ended': 'Ended {date}: {reason}',
		'operator.history.replaced': 'Replaced {date}',
		'operator.history.open': 'Open',
		'operator.section.grant': 'Grant an allowance',
		'operator.section.replace': 'Replace the allowance',
		'operator.field.plan': 'Plan',
		'operator.field.plan.hint': 'Lowercase letters, digits and hyphens, such as internal-pilot.',
		'operator.field.plan.wrong': 'Start with a letter and use only lowercase letters, digits and hyphens.',
		'operator.field.bytes': 'Storage, in MiB',
		'operator.field.bytes.hint': 'At most {max} MiB.',
		'operator.field.bytes.wrong': 'Write a whole number of MiB, at most {max}.',
		'operator.field.sources': 'Packages',
		'operator.field.sources.hint': 'At most {max}.',
		'operator.field.sources.wrong': 'Write a whole number, at most {max}.',
		'operator.field.ends': 'Ends on',
		'operator.field.ends.hint': 'Leave it empty to keep the allowance until an operator ends it. At most {days} days ahead.',
		'operator.field.ends.wrong': 'Choose a day after today, at most {days} days ahead.',
		'operator.field.reason': 'Reason',
		'operator.field.reason.hint': 'Kept with the allowance in its history.',
		'operator.field.reason.wrong': 'Write at most 500 characters.',
		'operator.review.grant': 'Review grant',
		'operator.review.title': 'Review before you send',
		'operator.review.reach': '{organization} can create and change cloud packages, up to {size} and {sources} packages, until {until}.',
		'operator.review.until.none': 'an operator ends the allowance',
		'operator.review.replaces': 'It replaces the allowance granted {date}.',
		'operator.review.charges': 'Nothing is charged: usage is measured.',
		'operator.review.undo': 'You can end it here at any time; its packages stay readable, downloadable and deletable.',
		'operator.confirm.grant': 'Grant allowance',
		'operator.confirm.replace': 'Replace allowance',
		'operator.edit': 'Edit terms',
		'operator.section.end': 'End the allowance',
		'operator.field.ending': 'Reason',
		'operator.field.ending.hint': 'Kept with the allowance in its history.',
		'operator.review.end': 'Review ending',
		'operator.review.end.reach': '{organization} can no longer create or change cloud packages, check them or take snapshots of them.',
		'operator.review.end.kept': 'Its packages stay readable, downloadable and deletable, and checks already asked still finish.',
		'operator.review.end.again': 'You can grant a new allowance here later.',
		'operator.confirm.end': 'End allowance',
		'operator.made.grant': 'Allowance granted.',
		'operator.made.grant.replayed': 'Your earlier request granted this allowance.',
		'operator.made.end': 'Allowance ended.',
		'operator.made.end.replayed': 'Your earlier request ended this allowance.',
		'operator.unknown.title': 'The change may have been made.',
		'operator.unknown.text': 'Check the allowance before you send it again. Sending the same request again never makes the change twice.',
		'operator.unknown.check': 'Check allowance',
		'operator.unknown.again': 'Send the same request again',
		'operator.recover.title': 'This change may have been made: its answer did not arrive',
		'operator.recover.forget': 'Forget this request',
		'operator.unavailable.title': 'Nothing was changed.',
		'operator.reauth.title': 'Sign in to Beyond again to change the allowance',
		'operator.reauth.text': 'Changing an allowance needs a sign-in from the last {minutes} minutes. Sign out of Beyond from your profile menu, sign in again and come back: your terms are kept.',
		'operator.expected.title': 'Another change came first',
		'operator.expected.text': 'The allowance changed after you read it. Your terms are kept: review them against the allowance in force now.',
		'operator.key.title': 'This request is not the one sent earlier',
		'operator.key.text': 'It differs from the request first sent under its key. Review it again to send it as a new request.',
		'operator.forbidden': 'Only a Beyond operator can change allowances.'
	},
	es: {
		'nav.operator': 'Asignaciones',
		'page.operator': 'Asignaciones de paquetes en la nube · Beyond Snapshots',
		'loading.operator': 'la asignación',
		'operator.title': 'Asignaciones de paquetes en la nube',
		'operator.lead': 'Activa los paquetes en la nube de una organización, cambia sus condiciones o desactívalos.',
		'operator.denied': 'Solo un operador de Beyond puede abrir esta página',
		'operator.denied.reason': 'Tu cuenta no es de un operador de Beyond en Beyond Accounts.',
		'operator.organization': 'Organización',
		'operator.organization.hint': 'Su identificador en Beyond Accounts, como org_…',
		'operator.organization.show': 'Mostrar organización',
		'operator.organization.wrong': 'Escribe un identificador como org_…, con letras y dígitos tras el guion bajo.',
		'operator.organization.none': 'Escribe el identificador de una organización para ver su asignación.',
		'operator.state.active': 'Activa en Beyond Accounts.',
		'operator.state.closing': 'Cerrándose en Beyond Accounts: no se puede conceder ninguna asignación.',
		'operator.state.closed': 'Cerrada en Beyond Accounts: no se puede conceder ninguna asignación.',
		'operator.state.unknown': 'Beyond Accounts no conoce esta organización como una que use Snapshots, así que no se puede conceder ninguna asignación. Antes, un miembro debe iniciar sesión en Snapshots.',
		'operator.state.unavailable': 'Beyond Accounts no dijo en qué estado está esta organización, así que ahora no se puede conceder ninguna asignación.',
		'operator.signed': 'Iniciaste sesión en Beyond {time}. Cambiar una asignación requiere un inicio de sesión de los últimos {minutes} minutos.',
		'operator.signed.unknown': 'Beyond Accounts no dijo cuándo iniciaste sesión. Cambiar una asignación requiere un inicio de sesión de los últimos {minutes} minutos.',
		'operator.section.current': 'Asignación vigente',
		'operator.current.none': 'Sin asignación. Los paquetes en la nube están desactivados para esta organización; sus paquetes se pueden seguir leyendo, descargando y eliminando.',
		'operator.current.past': 'Esta asignación terminó sola el {date}. Los paquetes en la nube están desactivados hasta que se conceda otra.',
		'operator.fact.plan': 'Plan',
		'operator.fact.bytes': 'Almacenamiento',
		'operator.fact.sources': 'Paquetes',
		'operator.fact.granted': 'Concedida',
		'operator.fact.granted.value': '{date} por {person}',
		'operator.fact.ends': 'Termina',
		'operator.fact.ends.none': 'Cuando un operador la termine',
		'operator.fact.reason': 'Motivo',
		'operator.limits.note': 'Es lo máximo que esta organización puede guardar. La instalación permite como máximo {bytes} y {sources} paquetes.',
		'operator.section.usage': 'Uso medido',
		'operator.usage.month': 'Mes',
		'operator.usage.lead': 'Lo que esta organización usó en {period}, leído {time}. El uso se mide; no se cobra nada.',
		'operator.usage.empty': 'No se midió nada en {period}.',
		'operator.usage.what': 'Qué',
		'operator.usage.quantity': 'Cantidad',
		'operator.usage.internal': '{kind} (trabajo de almacenamiento del propio servicio)',
		'operator.section.history': 'Historial',
		'operator.history.empty': 'Esta organización nunca tuvo una asignación.',
		'operator.history.granted': '{plan}, concedida {date} por {person}',
		'operator.history.ended': 'Terminada {date}: {reason}',
		'operator.history.replaced': 'Reemplazada {date}',
		'operator.history.open': 'Abierta',
		'operator.section.grant': 'Conceder una asignación',
		'operator.section.replace': 'Reemplazar la asignación',
		'operator.field.plan': 'Plan',
		'operator.field.plan.hint': 'Minúsculas, dígitos y guiones, como internal-pilot.',
		'operator.field.plan.wrong': 'Empieza con una letra y usa solo minúsculas, dígitos y guiones.',
		'operator.field.bytes': 'Almacenamiento, en MiB',
		'operator.field.bytes.hint': 'Como máximo {max} MiB.',
		'operator.field.bytes.wrong': 'Escribe un número entero de MiB, como máximo {max}.',
		'operator.field.sources': 'Paquetes',
		'operator.field.sources.hint': 'Como máximo {max}.',
		'operator.field.sources.wrong': 'Escribe un número entero, como máximo {max}.',
		'operator.field.ends': 'Termina el',
		'operator.field.ends.hint': 'Déjalo vacío para mantener la asignación hasta que un operador la termine. Como máximo {days} días después.',
		'operator.field.ends.wrong': 'Elige un día posterior a hoy, como máximo {days} días después.',
		'operator.field.reason': 'Motivo',
		'operator.field.reason.hint': 'Se guarda con la asignación en su historial.',
		'operator.field.reason.wrong': 'Escribe como máximo 500 caracteres.',
		'operator.review.grant': 'Revisar asignación',
		'operator.review.title': 'Revisa antes de enviar',
		'operator.review.reach': '{organization} puede crear y cambiar paquetes en la nube, hasta {size} y {sources} paquetes, hasta que {until}.',
		'operator.review.until.none': 'un operador termine la asignación',
		'operator.review.replaces': 'Reemplaza la asignación concedida {date}.',
		'operator.review.charges': 'No se cobra nada: el uso se mide.',
		'operator.review.undo': 'Puedes terminarla aquí cuando quieras; sus paquetes se pueden seguir leyendo, descargando y eliminando.',
		'operator.confirm.grant': 'Conceder asignación',
		'operator.confirm.replace': 'Reemplazar asignación',
		'operator.edit': 'Editar condiciones',
		'operator.section.end': 'Terminar la asignación',
		'operator.field.ending': 'Motivo',
		'operator.field.ending.hint': 'Se guarda con la asignación en su historial.',
		'operator.review.end': 'Revisar terminación',
		'operator.review.end.reach': '{organization} ya no puede crear ni cambiar paquetes en la nube, comprobarlos ni tomar instantáneas de ellos.',
		'operator.review.end.kept': 'Sus paquetes se pueden seguir leyendo, descargando y eliminando, y las comprobaciones ya pedidas terminan.',
		'operator.review.end.again': 'Más adelante puedes conceder aquí una nueva asignación.',
		'operator.confirm.end': 'Terminar asignación',
		'operator.made.grant': 'Asignación concedida.',
		'operator.made.grant.replayed': 'Tu solicitud anterior concedió esta asignación.',
		'operator.made.end': 'Asignación terminada.',
		'operator.made.end.replayed': 'Tu solicitud anterior terminó esta asignación.',
		'operator.unknown.title': 'Es posible que el cambio se haya hecho.',
		'operator.unknown.text': 'Comprueba la asignación antes de enviarlo otra vez. Enviar otra vez la misma solicitud nunca hace el cambio dos veces.',
		'operator.unknown.check': 'Comprobar asignación',
		'operator.unknown.again': 'Enviar otra vez la misma solicitud',
		'operator.recover.title': 'Es posible que este cambio se haya hecho: su respuesta no llegó',
		'operator.recover.forget': 'Olvidar esta solicitud',
		'operator.unavailable.title': 'No se cambió nada.',
		'operator.reauth.title': 'Vuelve a iniciar sesión en Beyond para cambiar la asignación',
		'operator.reauth.text': 'Cambiar una asignación requiere un inicio de sesión de los últimos {minutes} minutos. Cierra la sesión de Beyond desde tu menú de perfil, vuelve a iniciarla y regresa: tus condiciones se conservan.',
		'operator.expected.title': 'Otro cambio llegó antes',
		'operator.expected.text': 'La asignación cambió después de que la leíste. Tus condiciones se conservan: revísalas frente a la asignación vigente.',
		'operator.key.title': 'Esta solicitud no es la que se envió antes',
		'operator.key.text': 'Es distinta de la primera enviada con su clave. Revísala otra vez para enviarla como una solicitud nueva.',
		'operator.forbidden': 'Solo un operador de Beyond puede cambiar asignaciones.'
	}
};
